/* JavaScript (Vue 2) */
import Vue from '@vue/compat';

function isPlainObject(val) {
    return Object.prototype.toString.call(val) === '[object Object]';
}

function deepClone(val) {
    if (Array.isArray(val)) return val.map(deepClone);
    if (isPlainObject(val)) {
        const o = {};
        Object.keys(val).forEach((k) => {
            o[k] = deepClone(val[k]);
        });
        return o;
    }
    return val;
}

/**
 * Deeply merges source into target in a Vue-reactive way.
 * - Arrays are replaced (not merged) to ensure reactivity.
 * - Objects: recurse on existing plain objects; Vue.set for new keys.
 * - Primitives/functions/dates: assigned directly (Vue.set for new keys).
 *
 * Returns the target (possibly same reference).
 */
export function deepMerge(target, source) {
    if (target === source) return target;

    // If source is array: replace
    if (Array.isArray(source)) {
        return source.map(deepClone);
    }

    // If source is plain object
    if (isPlainObject(source)) {
        // If target isn't a plain object, replace whole branch
        const out = isPlainObject(target) ? target : {};

        Object.keys(source).forEach((key) => {
            const sVal = source[key];
            const hasKey = Object.prototype.hasOwnProperty.call(out, key);

            // Arrays: replace ref
            if (Array.isArray(sVal)) {
                const newArr = sVal.map(deepClone);
                if (hasKey) out[key] = newArr; else Vue.set(out, key, newArr);
                return;
            }

            // Objects: recurse or replace
            if (isPlainObject(sVal)) {
                if (hasKey && isPlainObject(out[key])) {
                    deepMerge(out[key], sVal);
                } else if (hasKey) {
                    out[key] = deepMerge({}, sVal);
                } else {
                    Vue.set(out, key, deepMerge({}, sVal));
                }
                return;
            }

            // Primitives and others: assign
            if (hasKey) out[key] = sVal; else Vue.set(out, key, sVal);
        });

        return out;
    }

    // Non-object source: caller should assign directly to parent slot
    return source;
}

/* Example usage:
   // Merge payload into an observed model instance:
   deepMerge(this, payload);

   // Merge a nested object:
   this.profile = deepMerge(this.profile, incomingProfile); // replace ref if needed
*/

export default deepMerge;
